import { PriceLabelSimple } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Offering, OfferingType, formatBillingFrequency } from '@/domain/offerings/offering'
import { BadgePlus, CalculatorIcon, Calendar, DollarSign, FileText, Hash, Layers, LucideIcon, Percent, RefreshCw, Tag, UserPlus, Users } from 'lucide-react'
import { getOfferingTypeIcon } from './common'

interface OfferingDetailsCardProps {
  offering: Offering
}

const PropLabel = ({ children, icon: Icon }: { children: React.ReactNode, icon?: LucideIcon }) => (
  <span className="text-sm text-muted-foreground font-bold leading-none mb-2 block flex items-center gap-1.5">
    {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
    {children}
  </span>
)

export const OfferingDetailsCard = ({ offering }: OfferingDetailsCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Detalles del Servicio</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-6">
          <div className='col-span-1'>
            <PropLabel icon={Tag}>Nombre</PropLabel>
            <p className="font-medium text-lg">
              {offering.name}
            </p>
          </div>
          <div className='col-span-1'>
            <PropLabel icon={FileText}>Descripción</PropLabel>
            <p className="text-muted-foreground">
              {offering.description || 'Sin descripción'}
            </p>
          </div>
          <div className='col-span-1'>
            <PropLabel icon={Hash}>Código</PropLabel>
            <p className="font-mono bg-muted w-fit px-2 py-0.5 rounded text-sm">{offering.code}</p>
          </div>
          <div className='flex sm:flex-row flex-col items-center gap-5 sm:gap-10'>
            <div>
              <PropLabel icon={Layers}>Tipo</PropLabel>
              <div className="flex items-center gap-2">
                {getOfferingTypeIcon(offering.offeringType)}
                <span className="capitalize">
                  {offering.offeringType === OfferingType.ONE_TIME ? 'Única vez' : 'Recurrente'}
                </span>
              </div>
            </div>
            {offering.offeringType === OfferingType.RECURRING && <div>
              <PropLabel icon={RefreshCw}>Frecuencia</PropLabel>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                {formatBillingFrequency(offering.billingFrequency)}
              </div>
            </div>}
          </div>
          <div>
            <PropLabel icon={DollarSign}>Precio base</PropLabel>
            <PriceLabelSimple price={offering.basePrice} />
          </div>
          <div>
            <PropLabel icon={Percent}>Impuesto</PropLabel>
            <p className="font-medium">{offering.taxRate ? `${offering.taxRate}% IVA incluído` : 'Sin IVA'}</p>
          </div>
          <div>
            <PropLabel icon={Users}>Contratos extensibles a esquema familiar</PropLabel>
            <Badge variant={offering.hasMultipleOffered ? 'default' : 'outline'}>
              {offering.hasMultipleOffered ? 'Sí' : 'No'}
            </Badge>
          </div>
          <div>
            <PropLabel icon={CalculatorIcon}>Planes de pago</PropLabel>
            <Badge variant={offering.hasPaymentPlanAvailable ? 'default' : 'outline'}>
              {offering.hasPaymentPlanAvailable ? 'Sí' : 'No'}
            </Badge>
          </div>
          <div>
            <PropLabel icon={UserPlus}>Precios por edad</PropLabel>
            <Badge variant={offering.hasAgePricing ? 'default' : 'outline'}>
              {offering.hasAgePricing ? 'Sí' : 'No'}
            </Badge>
          </div>
          <div>
            <PropLabel icon={BadgePlus}>Es servicio adicional de otro principal</PropLabel>
            <Badge variant={offering.isAdditional ? 'default' : 'outline'}>
              {offering.isAdditional ? 'Sí' : 'No'}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
